import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';
import { router } from '../../lib/router.js';

class AppsView extends BaseComponent {
    static properties = {
        _apps: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true },
        _formData: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _editId: { type: String, state: true },
        _confirmDelete: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
    `;

    constructor() {
        super();
        this._apps = [];
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._formData = { name: '', repo: '', todo_only: false };
        this._saving = false;
        this._editId = null;
        this._confirmDelete = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._unsubscribeRouter = router.subscribe((state) => {
            if (state.path === '/apps/new') {
                this._view = 'create';
                this._formData = { name: '', repo: '', todo_only: false };
                this._editId = null;
            } else if (state.params?.id) {
                this._startEdit(state.params.id);
            } else {
                this._view = 'list';
            }
        });
        this._loadAppsPromise = this._loadApps();
        const path = window.location.pathname;
        if (path === '/apps/new') {
            this._view = 'create';
        } else if (path.match(/\/apps\/[^/]+\/edit/)) {
            const id = path.split('/')[2];
            this._startEdit(id);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
    }

    async _loadApps() {
        this._loading = true;
        this._error = null;
        try {
            this._apps = await api.get('/api/apps');
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _startEdit(id) {
        await this._loadAppsPromise;
        const app = this._apps.find((a) => a.ID === id);
        if (app) {
            this._formData = {
                name: app.Name,
                repo: app.Repo,
                todo_only: app.TodoOnly,
            };
            this._editId = id;
            this._view = 'edit';
        }
    }

    async _submitCreate(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            await api.post('/api/apps', this._formData);
            await this._loadApps();
            router.navigate('/apps');
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _submitEdit(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            await api.put(`/api/apps/${this._editId}`, this._formData);
            await this._loadApps();
            router.navigate('/apps');
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _deleteApp(id) {
        this._error = null;
        try {
            await api.delete(`/api/apps/${id}`);
            this._confirmDelete = null;
            await this._loadApps();
        } catch (err) {
            this._error = err.message;
        }
    }

    _updateField(field, value) {
        this._formData = { ...this._formData, [field]: value };
    }

    render() {
        if (this._view === 'create')
            return this._renderForm('Add App', (e) => this._submitCreate(e));
        if (this._view === 'edit') return this._renderForm('Edit App', (e) => this._submitEdit(e));
        return this._renderList();
    }

    _renderList() {
        return html`
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Apps</h5>
                <a href="/apps/new" class="btn btn-primary btn-sm">
                    <i class="bi bi-plus-lg"></i> Add App
                </a>
            </div>

            <p class="text-muted" style="font-size: 0.875rem;">
                The catalog of apps <code>/d3po</code> can file issues against. Todo-only apps
                are areas offered only by <code>/d3po todo</code>. Edits take effect immediately —
                no redeploy needed.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            ${
                this._loading
                    ? html`
                <div class="text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>
            `
                    : html`
                ${
                    this._apps.length === 0
                        ? html`
                    <div class="text-muted text-center py-4">No apps configured.</div>
                `
                        : html`
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Repo</th>
                                <th>Todo only</th>
                                <th style="width: 100px;"></th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._apps.map(
                                (app) => html`
                                <tr>
                                    <td>${app.Name}</td>
                                    <td><code>${app.Repo}</code></td>
                                    <td>${app.TodoOnly ? html`<i class="bi bi-check-lg text-success"></i>` : ''}</td>
                                    <td class="text-end">
                                        <a href="/apps/${app.ID}/edit" class="btn btn-outline-secondary btn-sm me-1">
                                            <i class="bi bi-pencil"></i>
                                        </a>
                                        ${
                                            this._confirmDelete === app.ID
                                                ? html`
                                            <button class="btn btn-danger btn-sm" @click="${() => this._deleteApp(app.ID)}">
                                                Confirm
                                            </button>
                                        `
                                                : html`
                                            <button class="btn btn-outline-danger btn-sm" @click="${() => (this._confirmDelete = app.ID)}">
                                                <i class="bi bi-trash"></i>
                                            </button>
                                        `
                                        }
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                }
            `
            }
        `;
    }

    _renderForm(title, onSubmit) {
        return html`
            <div class="d-flex align-items-center gap-2 mb-3">
                <a href="/apps" class="btn btn-outline-secondary btn-sm">
                    <i class="bi bi-arrow-left"></i>
                </a>
                <h5 class="mb-0">${title}</h5>
            </div>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            <form @submit="${onSubmit}" style="max-width: 500px;">
                <div class="mb-3">
                    <label class="form-label">Name</label>
                    <input type="text" class="form-control" placeholder="e.g. rpgish"
                           .value="${this._formData.name}"
                           @input="${(e) => this._updateField('name', e.target.value)}"
                           required>
                    <div class="form-text">Display name shown in the <code>/d3po</code> app picker</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Repo</label>
                    <input type="text" class="form-control" placeholder="e.g. d3-rpgish"
                           .value="${this._formData.repo}"
                           @input="${(e) => this._updateField('repo', e.target.value)}"
                           required>
                    <div class="form-text">Short repo name (not full org/repo)</div>
                </div>

                <div class="mb-3 form-check">
                    <input type="checkbox" class="form-check-input" id="todo-only"
                           .checked="${this._formData.todo_only}"
                           @change="${(e) => this._updateField('todo_only', e.target.checked)}">
                    <label class="form-check-label" for="todo-only">Todo only</label>
                    <div class="form-text">Offer this app only via <code>/d3po todo</code> (an area, not a user-facing app)</div>
                </div>

                <div class="d-flex gap-2">
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                        ${this._saving ? html`<span class="spinner-border spinner-border-sm"></span>` : 'Save'}
                    </button>
                    <a href="/apps" class="btn btn-outline-secondary">Cancel</a>
                </div>
            </form>
        `;
    }
}

customElements.define('apps-view', AppsView);
